import { Context, Data, Duration, Effect, Schedule, ScopedCache } from 'effect';
import type * as Atom from 'effect/unstable/reactivity/Atom';
import type * as AtomRegistry from 'effect/unstable/reactivity/AtomRegistry';

import type { resolveListingCacheOptions } from './ListingCacheOptions';

/** Couples each snapshot with its activation marker for eviction. */
export class RetainedListingEntry extends Data.Class<{
  readonly atom: Atom.Atom<unknown>;
  readonly activated: Atom.Atom<unknown>;
}> {}

export class ListingRetention extends Context.Service<
  ListingRetention,
  {
    readonly retain: (entry: RetainedListingEntry) => Effect.Effect<void>;
  }
>()('@engine/ListingRetention') {}

/** Uses scoped LRU leases to retain atom values without a second freshness cache. */
export const makeListingRetention = (
  registry: AtomRegistry.AtomRegistry,
  settings: ReturnType<typeof resolveListingCacheOptions>
) =>
  Effect.gen(function* () {
    const cache = yield* ScopedCache.make({
      capacity: settings.capacity,
      timeToLive: settings.retentionTime,
      lookup: (entry: RetainedListingEntry) =>
        Effect.gen(function* () {
          yield* Effect.acquireRelease(
            Effect.sync(() => registry.mount(entry.activated)),
            (release) => Effect.sync(release)
          );
          yield* Effect.acquireRelease(
            Effect.sync(() => registry.mount(entry.atom)),
            (release) => Effect.sync(release)
          );
        }),
    });
    if (Duration.isFinite(settings.retentionTime)) {
      yield* ScopedCache.keys(cache).pipe(
        Effect.repeat(Schedule.spaced(settings.cleanupInterval)),
        Effect.forkScoped
      );
    }
    return ListingRetention.of({ retain: (entry) => ScopedCache.get(cache, entry) });
  });
